import { forwardRef } from "react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";

import { VehicleFault_Enum } from "@skydio/pbtypes/pbtypes/gen/common/fault";
import { isFragmentRef } from "@skydio/relay-toolkit";
import { Tag, Tooltip } from "@skydio/rivet-ui";

import type { ComponentPropsWithoutRef } from "react";
import type { UnpackedFragmentData } from "@skydio/relay-toolkit";
import type { ForwardRefComponentWithSubcomponents } from "@skydio/rivet-ui";
import type {
  VehicleBatteryWidgetV2_vehicle$data,
  VehicleBatteryWidgetV2_vehicle$key,
} from "../../__generated__/VehicleBatteryWidgetV2_vehicle.graphql";

const BATTERY_NOT_CHARGING_TOO_HOT_FAULT: VehicleFault_Enum =
  VehicleFault_Enum.BATTERY_NOT_CHARGING_TOO_HOT;
const BATTERY_NOT_CHARGING_TOO_COLD_FAULT: VehicleFault_Enum =
  VehicleFault_Enum.BATTERY_NOT_CHARGING_TOO_COLD;

const GREEN_COLOR = "#22C55E";
const YELLOW_COLOR = "#FFBE18";
const RED_COLOR = "#FF0000";

/**
 * Relay fragment defining the GraphQL data needed to render a VehicleBatteryWidgetV2.
 */
const vehicleFragment = graphql`
  fragment VehicleBatteryWidgetV2_vehicle on Vehicle {
    id
    isOnline
    isOnlineMobile
    vehicleId
    batteryStatus {
      percentage
      charging
    }
    activeFaults
  }
`;

/**
 * Additional props (aside from fragment data) used to render the VehicleBatteryWidgetV2 component.
 */
type BaseProps = ComponentPropsWithoutRef<"div">;

/**
 * Fragment reference type for VehicleBatteryWidgetV2. No need to modify this.
 */
export type VehicleBatteryWidgetV2Fragment = VehicleBatteryWidgetV2_vehicle$key;
/**
 * Fragment data type for VehicleBatteryWidgetV2. Modify only if this component composes other entity components
 * with their own fragments. See the Storybook guide on Entity Components for more information.
 */
export type VehicleBatteryWidgetV2Data = UnpackedFragmentData<VehicleBatteryWidgetV2_vehicle$data>;

interface InnerProps extends BaseProps {
  vehicleData: VehicleBatteryWidgetV2Data;
}

/**
 * Inner component for VehicleBatteryWidgetV2. This component receives the fragment data as a prop and is
 * responsible for rendering the actual UI.
 */
const VehicleBatteryWidgetV2Inner = forwardRef<HTMLDivElement, InnerProps>(
  ({ vehicleData, ...props }, ref) => {
    const { isOnline, isOnlineMobile, batteryStatus, activeFaults } = vehicleData;

    if (!isOnline && !isOnlineMobile) {
      return (
        <div ref={ref} {...props}>
          <Tag color="default" variant="bordered" size="sm">
            Unknown
          </Tag>
        </div>
      );
    }

    // Battery-related information
    const percentage = (batteryStatus?.percentage ?? 0) * 100;
    const charging = batteryStatus?.charging ?? false;

    // Clamp percentage between 0 and 100 and round to the nearest whole number
    const clampedPercentage = Math.round(Math.max(0, Math.min(percentage, 100)));
    const isHot = activeFaults?.includes(BATTERY_NOT_CHARGING_TOO_HOT_FAULT);
    const isCold = activeFaults?.includes(BATTERY_NOT_CHARGING_TOO_COLD_FAULT);

    // Define container dimensions and gaps
    const containerWidth = 16; // Total inner width of the battery container
    const leftGap = 1; // Gap between the battery container and the fill area
    const rightGap = 1;
    const maxFillWidth = containerWidth - leftGap - rightGap; // Max width of the fill area

    const fillWidth = (clampedPercentage / 100) * maxFillWidth; // NOTE: doesn't apply for charging, hot, or cold states

    const batteryColor =
      clampedPercentage >= 70 ? GREEN_COLOR : clampedPercentage >= 30 ? YELLOW_COLOR : RED_COLOR;

    if (isHot || isCold) {
      return (
        <Tooltip
          showArrow
          closeDelay={50}
          placement="top"
          content={
            isHot
              ? "High battery temperature: charging is disabled and will resume when battery cools"
              : "Low battery temperature: charging is disabled and will resume when battery warms"
          }
        >
          <div className="flex items-center gap-1">
            <span className="text-sm text-gray-900">{clampedPercentage}%</span>
            <svg
              width="18"
              height="11"
              viewBox="0 0 18 11"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Battery */}
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M2.53836 9.1625C2.57611 9.33909 2.64141 9.50987 2.73285 9.66752L2.73878 9.67761C2.94005 10.0155 3.25594 10.2821 3.63683 10.4125H2.0375C1.225 10.4125 0.5625 9.75 0.5625 8.9375V1.5375C0.5625 0.725 1.225 0.0625 2.0375 0.0625H7.69106C7.27131 0.18141 6.90557 0.464094 6.68882 0.859621L6.43576 1.3125H2.0375C1.9125 1.3125 1.8125 1.4125 1.8125 1.5375V8.9375C1.8125 9.0625 1.9125 9.1625 2.0375 9.1625H2.53836ZM13.7422 9.1625C13.7043 9.33963 13.6388 9.51052 13.5478 9.66752L13.5419 9.67764C13.3406 10.0155 13.0247 10.2821 12.6438 10.4125H14.1375C14.9625 10.4125 15.625 9.75 15.625 8.9375V7.6875H15.9375C16.575 7.6875 17.0875 7.175 17.0875 6.5375V3.9375C17.0875 3.3 16.575 2.7875 15.9375 2.7875H15.625V1.5375C15.625 0.725 14.9625 0.0625 14.1375 0.0625H8.58932C9.01402 0.183363 9.37465 0.471947 9.59047 0.861669L9.84238 1.3125H14.1375C14.2625 1.3125 14.375 1.4125 14.375 1.5375V8.9375C14.375 9.0625 14.2625 9.1625 14.1375 9.1625H13.7422ZM10.2266 2H13.45C13.575 2 13.6625 2.1 13.6625 2.2125V8.17811C13.6326 8.10286 13.597 8.02903 13.5554 7.95724L10.2266 2ZM6.05159 2L2.72359 7.95577C2.65624 8.07344 2.60448 8.1966 2.5678 8.32261C2.56432 8.30717 2.5625 8.29122 2.5625 8.275V2.2125C2.5625 2.0875 2.6625 2 2.775 2H6.05159Z"
                fill={batteryColor}
              />
              {/* Warning */}
              <path
                d="M8.06993 7.27936C7.74453 7.27936 7.48069 7.5432 7.48069 7.8686C7.48069 8.194 7.74453 8.45784 8.06993 8.45784C8.39533 8.45784 8.65917 8.194 8.65917 7.8686C8.65917 7.5432 8.39533 7.27936 8.06993 7.27936ZM12.1155 9.5H4.16512C3.93206 9.5 3.72099 9.37248 3.59787 9.1658C3.47035 8.94594 3.46595 8.66891 3.59347 8.44904L7.56424 1.34299C7.67857 1.13192 7.90283 1 8.14029 1C8.37774 1 8.59761 1.13192 8.71634 1.34739L12.6871 8.45344C12.8146 8.66891 12.8102 8.94594 12.6827 9.1658C12.5596 9.37248 12.3485 9.5 12.1155 9.5ZM7.6302 6.0745C7.6302 6.31735 7.82708 6.51423 8.06993 6.51423C8.31279 6.51423 8.50966 6.31735 8.50966 6.0745V3.64278C8.50966 3.39993 8.31279 3.20305 8.06993 3.20305C7.82708 3.20305 7.6302 3.39993 7.6302 3.64278V6.0745Z"
                fill="#FFB700"
              />
            </svg>
          </div>
        </Tooltip>
      );
    }

    if (charging) {
      return (
        <div ref={ref} {...props}>
          <div className="flex items-center gap-1">
            <span className="text-sm text-gray-900">{clampedPercentage}%</span>
            <svg
              width="18"
              height="11"
              viewBox="0 0 18 11"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* Lightning Bolt */}
              <path
                d="M9.22495 4.5875V0.0625H9.18745L8.31245 1.3125L7.84995 1.9875L5.13745 5.9H7.57495V10.275L8.34995 9.1625L8.81245 8.4875L11.5125 4.5875H9.22495Z"
                fill={batteryColor}
              />
              {/* Battery */}
              <path
                d="M1.8125 1.5375V8.9375C1.8125 9.0625 1.9125 9.1625 2.0375 9.1625H6.325V10.4125H2.0375C1.225 10.4125 0.5625 9.75 0.5625 8.9375V1.5375C0.5625 0.725 1.225 0.0625 2.0375 0.0625H7.6625L6.8 1.3125H2.0375C1.9125 1.3125 1.8125 1.4125 1.8125 1.5375ZM2.75 7.15L6.325 1.9875H2.775C2.65 1.9875 2.5625 2.0875 2.5625 2.2125V8.275C2.5625 8.3875 2.65 8.4875 2.775 8.4875H6.325V7.15H2.75ZM13.6625 3.675L10.325 8.4875H13.45C13.575 8.4875 13.6625 8.3875 13.6625 8.275V3.675ZM13.45 1.9875H10.475V3.3375H13.6625V2.2125C13.6625 2.0875 13.575 1.9875 13.45 1.9875ZM15.625 7.6875H15.9375C16.575 7.6875 17.0875 7.175 17.0875 6.5375V3.9375C17.0875 3.3 16.575 2.7875 15.9375 2.7875H15.625V1.5375C15.625 0.725 14.9625 0.0625 14.15 0.0625H10.475V1.3125H14.15C14.275 1.3125 14.375 1.4125 14.375 1.5375V8.9375C14.375 9.0625 14.275 9.1625 14.15 9.1625H9.85L8.9875 10.4125H14.15C14.9625 10.4125 15.625 9.75 15.625 8.9375V7.6875Z"
                fill={batteryColor}
              />
            </svg>
          </div>
        </div>
      );
    }

    // Default battery state (w/ dynamic fill width depending on percentage)
    return (
      <div ref={ref} {...props}>
        <div className="flex items-center gap-1">
          <span className="text-sm text-gray-900">{clampedPercentage}%</span>
          <svg
            width="18"
            height="11"
            viewBox="0 0 18 11"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Battery Outer Frame */}
            <rect
              x="0.5"
              y="1"
              width="16"
              height="9"
              rx="1"
              fill="none"
              stroke={batteryColor}
              strokeWidth="1"
            />
            {/* Battery Terminal */}
            <rect x="16.5" y="3" width="1.5" height="5" fill={batteryColor} />
            {/* Fill Area */}
            <rect x={0.5 + leftGap} y="2" width={fillWidth} height="7" fill={batteryColor} />
          </svg>
        </div>
      </div>
    );
  }
);
VehicleBatteryWidgetV2Inner.displayName = "VehicleBatteryWidgetV2.Inner";

/*************** WRAPPER COMPONENT BOILERPLATE BELOW - DO NOT MODIFY **************
 *
 * If your component renders something other than a div, you should update the element type
 * in each of the `forwardRef` calls in this file accordingly. Otherwise, there should be no need
 * to modify the rest of this file.
 */

interface WrapperProps extends BaseProps {
  vehicleRef: VehicleBatteryWidgetV2Fragment;
}

/**
 * Wrapper component for VehicleBatteryWidgetV2. This component is responsible for unpacking the data
 * associated with the provided fragment reference and passing it to the inner component.
 */
const VehicleBatteryWidgetV2Wrapper = forwardRef<HTMLDivElement, WrapperProps>(
  ({ vehicleRef, ...props }, ref) => {
    const vehicleData = useFragment(vehicleFragment, vehicleRef);

    return <VehicleBatteryWidgetV2Inner vehicleData={vehicleData} {...props} ref={ref} />;
  }
);
VehicleBatteryWidgetV2Wrapper.displayName = "VehicleBatteryWidgetV2.Wrapper";

export type VehicleBatteryWidgetV2Props = BaseProps & {
  vehicle: VehicleBatteryWidgetV2Data | VehicleBatteryWidgetV2Fragment;
};

/**
 * "Umbrella" component for VehicleBatteryWidgetV2. This component is responsible for determining whether the
 * provided `vehicle` prop is a fragment reference or fragment data, and rendering the appropriate
 * component accordingly.
 */
export const VehicleBatteryWidgetV2 = forwardRef<HTMLDivElement, VehicleBatteryWidgetV2Props>(
  ({ vehicle, ...props }, ref) => {
    if (isFragmentRef(vehicle, "VehicleBatteryWidgetV2_vehicle")) {
      return <VehicleBatteryWidgetV2Wrapper vehicleRef={vehicle} {...props} ref={ref} />;
    }
    return <VehicleBatteryWidgetV2Inner vehicleData={vehicle} {...props} ref={ref} />;
  }
) as ForwardRefComponentWithSubcomponents<
  HTMLDivElement,
  {
    Inner: typeof VehicleBatteryWidgetV2Inner;
    Wrapper: typeof VehicleBatteryWidgetV2Wrapper;
  },
  VehicleBatteryWidgetV2Props
>;
VehicleBatteryWidgetV2.displayName = "VehicleBatteryWidgetV2";
